// src/components/routes/Other/UCP/UCP.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../../../../utils/api';
import { getStoredSuperAdminCompany } from '../../../../utils/superAdminCompany';
import { UCP_DEMO_LOGIN_URL, UCP_IN_LOGIN_URL, UCP_MED_LOGIN_URL, UCP_OCUBE_LOGIN_URL, normalizeDialPhone } from '../../../../utils/ucpDialer';
import './UCP.css';

const UCP_POPUP_GAP = 12;
const UCP_BUTTON_CLEARANCE = 76;
const UCP_MAX_HEIGHT = 720;
const UCP_DIRECT_CALL_BUTTON_XPATH = '/html/body/div/div/div[3]/button';
const UCP_LOGIN_OPTIONS = [
  { label: 'Open UCP Demo', url: UCP_DEMO_LOGIN_URL, className: 'ucp-popup-btn-demo' },
  { label: 'Open UCP IN', url: UCP_IN_LOGIN_URL, className: 'ucp-popup-btn-in' },
  { label: 'Open UCP MED', url: UCP_MED_LOGIN_URL, className: 'ucp-popup-btn-med' },
  { label: 'Open UCP OCube', url: UCP_OCUBE_LOGIN_URL, className: 'ucp-popup-btn-ocube' }
];


const getConfiguredUcpOption = (url) => (
  UCP_LOGIN_OPTIONS.find(option => option.url === url) || UCP_LOGIN_OPTIONS[0]
);

const getInitialUcpOption = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const selectedCompany = getStoredSuperAdminCompany();
    const selectedUrl = user.role === 'super_admin'
      ? selectedCompany?.ucp_login_url || user.ucp_login_url
      : user.ucp_login_url;
    return getConfiguredUcpOption(selectedUrl);
  } catch {
    return UCP_LOGIN_OPTIONS[0];
  }
};

const UCP = ({ isLoggedIn }) => { 
  const isAuthenticated = Boolean(isLoggedIn);
  const [showPopup, setShowPopup] = useState(false);
  const [configuredUcp, setConfiguredUcp] = useState(getInitialUcpOption);
  const [activeLoginUrl, setActiveLoginUrl] = useState(() => getInitialUcpOption().url);
  const popupContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const pendingCallDestinationRef = useRef('');
  const pendingCallSentRef = useRef(false);
  const lastOpenedDialRef = useRef({ number: '', at: 0 });
  const directCallListenerCleanupRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const handleOpenPopup = useCallback((loginUrl = configuredUcp.url) => {
    const containerHeight = Math.min(UCP_MAX_HEIGHT, window.innerHeight - (UCP_BUTTON_CLEARANCE + UCP_POPUP_GAP));
    const bottomPosition = Math.max(UCP_POPUP_GAP, window.innerHeight - containerHeight - UCP_BUTTON_CLEARANCE);

    setActiveLoginUrl(loginUrl);
    setPosition({
      x: UCP_POPUP_GAP,
      y: bottomPosition
    });
    setShowPopup(true);
  }, [configuredUcp.url]);

  const postMakeCallMessage = useCallback((destination) => {
    const iframe = iframeRef.current || document.getElementById('ucp-iframe');

    if (!iframe?.contentWindow || !destination) {
      return false;
    }

    iframe.contentWindow.postMessage({
      type: 'MAKE_CALL',
      payload: { destination }
    }, '*');

    console.info('[CRM Dialer] Sent one dial request to UCP iframe.', {
      destination,
      iframeSrc: iframe.src,
      pageOrigin: window.location.origin
    });

    return true;
  }, []);

  const flushPendingCall = useCallback((destination = pendingCallDestinationRef.current) => {
    if (!destination) {
      return false;
    }

    if (pendingCallSentRef.current && pendingCallDestinationRef.current === destination) {
      console.info('[CRM Dialer] Duplicate UCP iframe dial request blocked.', { destination });
      return true;
    }

    const sent = postMakeCallMessage(destination);
    if (sent && pendingCallDestinationRef.current === destination) {
      pendingCallSentRef.current = true;
      pendingCallDestinationRef.current = '';
    }

    return sent;
  }, [postMakeCallMessage]);

  const makeCall = useCallback((destination) => {
    if (!isAuthenticated) {
      return false;
    }

    const normalizedDestination = normalizeDialPhone(destination);

    if (!normalizedDestination) {
      return false;
    }

    pendingCallDestinationRef.current = normalizedDestination;
    pendingCallSentRef.current = false;

    if (!showPopup) {
      handleOpenPopup();
    }

    window.setTimeout(() => flushPendingCall(normalizedDestination), 100);

    return true;
  }, [flushPendingCall, handleOpenPopup, isAuthenticated, showPopup]);

  const openCrmRecordForDialedNumber = useCallback(async (rawNumber) => {
    const number = String(rawNumber || '').replace(/\D/g, '');
    if (!number) return false;

    const now = Date.now();
    if (lastOpenedDialRef.current.number === number && now - lastOpenedDialRef.current.at < 2500) return true;
    lastOpenedDialRef.current = { number, at: now };

    try {
      await api.get(`/customers/phone/${encodeURIComponent(number)}`);
      window.location.assign(`/customers/phone/${number}`);
    } catch (error) {
      if (error.response?.status === 404) {
        sessionStorage.setItem('ucpPendingCustomerPhone', number);
        window.location.assign('/customer/new');
      } else {
        console.warn('[CRM Dialer] Customer lookup failed.', error.response?.data || error.message);
      }
    }
    return true;
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const eventPhone = data => {
      if (!data || typeof data !== 'object') return '';
      const payload = data.payload && typeof data.payload === 'object' ? data.payload : {};
      const call = data.call && typeof data.call === 'object' ? data.call : {};
      return data.destination || data.phone || data.phoneNumber || data.number || data.callee
        || payload.destination || payload.phone || payload.phoneNumber || payload.number || payload.callee
        || call.destination || call.phone || call.phoneNumber || call.number || call.callee || '';
    };
    const handleUcpMessage = event => {
      if (iframeRef.current?.contentWindow && event.source !== iframeRef.current.contentWindow) return;
      let data = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      const type = String(data?.type || data?.event || data?.action || '').toUpperCase();
      const isCallEvent = type.includes('CALL') || type.includes('DIAL');
      if (!isCallEvent) return;
      const number = eventPhone(data);
      if (number) openCrmRecordForDialedNumber(number);
    };

    window.addEventListener('message', handleUcpMessage);
    return () => window.removeEventListener('message', handleUcpMessage);
  }, [isAuthenticated, openCrmRecordForDialedNumber]);

  const attachDirectUcpCallListener = useCallback(() => {
    const frame = iframeRef.current;
    let doc;
    try {
      doc = frame?.contentDocument || frame?.contentWindow?.document;
    } catch {
      return undefined;
    }
    if (!doc) return undefined;

    const onClick = event => {
      let callButton = null;
      try {
        callButton = doc.evaluate(UCP_DIRECT_CALL_BUTTON_XPATH, doc, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
      } catch {
        return;
      }
      if (!callButton || (event.target !== callButton && !callButton.contains(event.target))) return;
      const inputs = [...doc.querySelectorAll('input[type="tel"], input[type="text"], input:not([type])')];
      const phoneInput = inputs.reverse().find(input => String(input.value || '').replace(/\D/g, '').length >= 10);
      if (phoneInput) openCrmRecordForDialedNumber(phoneInput.value);
    };
    doc.addEventListener('click', onClick, true);
    return () => doc.removeEventListener('click', onClick, true);
  }, [openCrmRecordForDialedNumber]);

  const handleUcpFrameLoad = useCallback(() => {
    flushPendingCall();
    directCallListenerCleanupRef.current?.();
    directCallListenerCleanupRef.current = attachDirectUcpCallListener() || null;
  }, [attachDirectUcpCallListener, flushPendingCall]);

  useEffect(() => () => directCallListenerCleanupRef.current?.(), []);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowPopup(false);
      return undefined;
    }

    let isMounted = true;

    const fetchConfiguredUcp = async () => {
      try {
        const response = await api.get('/current-user');
        const userData = response.data || {};
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const mergedUser = { ...storedUser, ...userData };
        localStorage.setItem('user', JSON.stringify(mergedUser));

        const selectedCompany = getStoredSuperAdminCompany();
        const selectedUrl = mergedUser.role === 'super_admin'
          ? selectedCompany?.ucp_login_url || mergedUser.ucp_login_url
          : mergedUser.ucp_login_url;
        const nextOption = getConfiguredUcpOption(selectedUrl);

        if (isMounted) {
          setConfiguredUcp(nextOption);
          setActiveLoginUrl(current => (showPopup ? current : nextOption.url));
        }
      } catch (error) {
        const fallbackOption = getInitialUcpOption();
        if (isMounted) {
          setConfiguredUcp(fallbackOption);
          setActiveLoginUrl(current => (showPopup ? current : fallbackOption.url));
        }
      }
    };

    fetchConfiguredUcp();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, showPopup]);

  const handleMinimize = () => {
    setShowPopup(false);
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.ucp-popup-header') && !e.target.closest('button')) {
      setIsDragging(true);
      const rect = popupContainerRef.current.getBoundingClientRect();
      setStartPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && popupContainerRef.current) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const containerWidth = popupContainerRef.current.offsetWidth;
      const containerHeight = popupContainerRef.current.offsetHeight;

      let x = e.clientX - startPos.x;
      let y = e.clientY - startPos.y;

      // Ensure the container stays within viewport bounds
      if (x < 0) x = 0;
      if (y < 0) y = 0;
      if (x + containerWidth > viewportWidth) x = viewportWidth - containerWidth;
      if (y + containerHeight > viewportHeight) y = viewportHeight - containerHeight;

      setPosition({ x, y });
    }
  }, [isDragging, startPos]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (window.makeCall === makeCall) {
        delete window.makeCall;
      }
      return undefined;
    }

    window.makeCall = makeCall;

    return () => {
      if (window.makeCall === makeCall) {
        delete window.makeCall;
      }
    };
  }, [isAuthenticated, makeCall]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const handleDialCustomer = (event) => {
      makeCall(event.detail?.destination);
    };

    window.addEventListener('crm:dial-customer', handleDialCustomer);

    if (window.__crmPendingDialDestination) {
      makeCall(window.__crmPendingDialDestination);
      window.__crmPendingDialDestination = '';
    }

    return () => {
      window.removeEventListener('crm:dial-customer', handleDialCustomer);
    };
  }, [isAuthenticated, makeCall]);

  useEffect(() => {
    if (!showPopup) {
      return undefined;
    }

    const keepPopupInViewport = () => {
      if (!popupContainerRef.current) {
        return;
      }

      const containerWidth = popupContainerRef.current.offsetWidth;
      const containerHeight = popupContainerRef.current.offsetHeight;

      setPosition((current) => ({
        x: Math.min(Math.max(current.x, 0), Math.max(window.innerWidth - containerWidth, 0)),
        y: Math.min(Math.max(current.y, 0), Math.max(window.innerHeight - containerHeight, 0))
      }));
    };

    window.addEventListener('resize', keepPopupInViewport);
    keepPopupInViewport();

    return () => window.removeEventListener('resize', keepPopupInViewport);
  }, [showPopup]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <div className="ucp-popup-actions">
        <button 
          className={`ucp-popup-btn ${configuredUcp.className}`} 
          onClick={() => handleOpenPopup(configuredUcp.url)}
        >
          {configuredUcp.label}
        </button>
      </div>

      <div 
        className={`ucp-popup-container ${!showPopup ? 'ucp-hidden' : ''}`}
        ref={popupContainerRef}
        style={{
          transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
          position: 'fixed',
          top: 0,
          left: 0
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="ucp-popup-header">
          <button 
            className="ucp-minimize-btn" 
            onClick={handleMinimize}
            title="Minimize"
          >
            -
          </button>
        </div>
        <iframe 
          id="ucp-iframe"
          ref={iframeRef}
          src={activeLoginUrl} 
          title="ucp"
          className="ucp-iframe"
          allow="notifications; microphone"
          onLoad={handleUcpFrameLoad}
          style={{ display: showPopup ? 'block' : 'none' }}
        />
      </div>
    </>
  );
};

export default UCP;
